import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '@/services/api';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const id = setInterval(() => setCooldownSeconds((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldownSeconds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');
    try {
      await api.requestPasswordReset(email.trim());
      setStatus('sent');
      setCooldownSeconds(120);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429 && e.retryAfterSeconds != null) {
        setCooldownSeconds(e.retryAfterSeconds);
        setMessage(`Please wait ${e.retryAfterSeconds} seconds before requesting another email.`);
        setStatus('error');
      } else {
        setMessage(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
        setStatus('error');
      }
    }
  };

  if (status === 'sent') {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="bg-wall-surface border border-wall-border rounded-lg p-8 w-full max-w-md text-center">
          <div className="text-4xl text-wall-accent mb-4">✉</div>
          <h1 className="text-2xl font-bold text-wall-text mb-2">Check your email</h1>
          <p className="text-wall-muted mb-6">
            If an account exists with <strong className="text-wall-text">{email}</strong>, you will
            receive a password reset link shortly. The link expires in 1 hour.
          </p>
          <p className="text-wall-muted text-sm mb-6">
            Didn’t get it? Check spam, or request another link after the cooldown.
          </p>
          <Link
            to="/login"
            className="inline-block bg-wall-accent text-wall-bg px-6 py-2 rounded-sm font-medium hover:opacity-90"
          >
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full p-4">
      <div className="bg-wall-surface border border-wall-border rounded-lg p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-center mb-2">Forgot your password?</h1>
        <p className="text-wall-muted text-center text-sm mb-6">
          Enter your email and we’ll send you a link to reset it.
        </p>

        {message && (
          <div
            className={`mb-4 px-4 py-2 rounded-sm text-sm ${
              status === 'error'
                ? 'bg-red-900/50 border border-red-500 text-red-200'
                : 'bg-wall-card border border-wall-border text-wall-muted'
            }`}
          >
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-wall-muted mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-wall-bg border border-wall-border rounded-sm focus:outline-hidden focus:ring-2 focus:ring-wall-accent"
              placeholder="you@example.com"
              required
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={status === 'loading' || cooldownSeconds > 0}
            className="w-full py-2 bg-wall-accent hover:bg-opacity-80 rounded-sm font-medium transition-colors disabled:opacity-50"
          >
            {status === 'loading'
              ? 'Sending…'
              : cooldownSeconds > 0
                ? `Request again in ${cooldownSeconds}s`
                : 'Send reset link'}
          </button>
        </form>

        <p className="text-center text-wall-muted mt-6 text-sm">
          <Link to="/login" className="text-wall-accent hover:underline">
            ← Back to login
          </Link>
        </p>
      </div>
    </div>
  );
}
